// #1167 RU add-carrier modal — two tabs (inline / fromCatalog).
//
// Tabs (labels come from i18n key `drawer.carriers.add.modeInline` /
// `drawer.carriers.add.modeFromCatalog`):
//   - Inline — name + contacts inputs; POSTs
//     `{ kind: 'inline', name, contacts }`. The service creates a fresh
//     `RussiaCarrier` directory entry (active=true) and snapshots its
//     name/contacts onto the join row.
//   - From catalog — picks an existing `RussiaCarrier` (active=true
//     only) and POSTs `{ kind: 'fromCatalog', russiaCarrierId }`. The
//     service copies the directory row's name/contacts into the snapshot.
//
// @aiPurpose modal arbitration + wire-shape adapter; presentational
//   only. Mutations fan out via `useAddRussiaRequestCarrier`.
// @aiSecurity caller must hold `russiaCarriers.manage` — gated at
//   `CarriersTab` (the modal does not paint without `canEdit`).

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorCode } from '@magimp/shared';

import { ApiClientError } from '../../../../api/errors.js';
import {
  Button,
  EditorModal,
  FormRow,
  Input,
  Select,
  Textarea,
  useEditorGuard,
} from '../../../../ui/index.js';
import { useAddRussiaRequestCarrier, useRussiaCarriersCatalog } from '../../queries.js';

type Mode = 'inline' | 'fromCatalog';

type AddErrorKey =
  | 'drawer.carriers.add.notFound'
  | 'drawer.carriers.add.invalidStatus'
  | 'drawer.carriers.add.generic';

function addErrorKey(err: unknown): AddErrorKey {
  if (err instanceof ApiClientError) {
    if (err.code === ErrorCode.NOT_FOUND) return 'drawer.carriers.add.notFound';
    if (err.code === ErrorCode.INVALID_TRANSITION) return 'drawer.carriers.add.invalidStatus';
  }
  return 'drawer.carriers.add.generic';
}

export interface RussiaAddCarrierModalProps {
  russiaRequestId: string;
  onClose: () => void;
}

export function RussiaAddCarrierModal({ russiaRequestId, onClose }: RussiaAddCarrierModalProps) {
  const { t } = useTranslation(['russiaRequests', 'common']);
  const [mode, setMode] = useState<Mode>('inline');

  const [name, setName] = useState('');
  const [contacts, setContacts] = useState('');
  const [pickedId, setPickedId] = useState<string>('');

  const catalog = useRussiaCarriersCatalog({ enabled: mode === 'fromCatalog' });
  const addMutation = useAddRussiaRequestCarrier(russiaRequestId);

  const catalogOptions = useMemo(() => {
    const items = catalog.data?.items ?? [];
    return items.filter((c) => c.active).map((c) => ({ value: c.id, label: c.name }));
  }, [catalog.data]);

  const isInlineValid = name.trim().length > 0 && contacts.trim().length > 0;
  const isFromCatalogValid = pickedId.length > 0;
  const canSubmit = mode === 'inline' ? isInlineValid : isFromCatalogValid;
  const dirty = mode === 'inline' ? isInlineValid : isFromCatalogValid;

  async function onSubmit(): Promise<void> {
    if (!canSubmit) return;
    try {
      if (mode === 'inline') {
        await addMutation.mutateAsync({
          kind: 'inline',
          name: name.trim(),
          contacts: contacts.trim(),
        });
      } else {
        await addMutation.mutateAsync({ kind: 'fromCatalog', russiaCarrierId: pickedId });
      }
      onClose();
    } catch {
      // WHY: error surface is rendered from `addMutation.error`.
    }
  }

  return (
    <EditorModal
      open
      dirty={dirty}
      busy={addMutation.isPending}
      onConfirmClose={onClose}
      title={t('drawer.carriers.add.modalTitle')}
      width={460}
    >
      <div data-testid="russia-request-drawer-carriers-add" style={{ display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            kind={mode === 'inline' ? 'primary' : 'ghost'}
            size="sm"
            data-testid="russia-add-carrier-mode-inline"
            onClick={() => setMode('inline')}
          >
            {t('drawer.carriers.add.modeInline')}
          </Button>
          <Button
            kind={mode === 'fromCatalog' ? 'primary' : 'ghost'}
            size="sm"
            data-testid="russia-add-carrier-mode-from-catalog"
            onClick={() => setMode('fromCatalog')}
          >
            {t('drawer.carriers.add.modeFromCatalog')}
          </Button>
        </div>

        {mode === 'inline' ? (
          <>
            <div data-testid="russia-add-carrier-name-input">
              <FormRow label={t('drawer.carriers.add.nameLabel')}>
                <Input
                  value={name}
                  onChange={setName}
                  ariaLabel={t('drawer.carriers.add.nameLabel')}
                />
              </FormRow>
            </div>
            <div data-testid="russia-add-carrier-contacts-input">
              <FormRow label={t('drawer.carriers.add.contactsLabel')}>
                <Textarea
                  value={contacts}
                  onChange={setContacts}
                  rows={3}
                  ariaLabel={t('drawer.carriers.add.contactsLabel')}
                />
              </FormRow>
            </div>
          </>
        ) : (
          <div data-testid="russia-add-carrier-catalog-select">
            <FormRow label={t('drawer.carriers.add.catalogPlaceholder')}>
              <Select
                value={pickedId}
                placeholder={t('drawer.carriers.add.catalogPlaceholder')}
                options={catalogOptions}
                onChange={setPickedId}
              />
            </FormRow>
          </div>
        )}

        {catalog.isError && mode === 'fromCatalog' && (
          <span
            data-testid="russia-request-drawer-carriers-catalog-error"
            style={{ fontSize: 12, color: 'var(--status-error-fg)' }}
          >
            {t('drawer.carriers.add.catalogError')}
          </span>
        )}
        {addMutation.isError && (
          <span
            role="alert"
            data-testid="russia-request-drawer-carriers-add-error"
            style={{ fontSize: 12, color: 'var(--status-error-fg)' }}
          >
            {t(addErrorKey(addMutation.error))}
          </span>
        )}

        <AddModalFooter
          onClose={onClose}
          onSubmit={onSubmit}
          canSubmit={canSubmit && !addMutation.isPending}
        />
      </div>
    </EditorModal>
  );
}

function AddModalFooter({
  onClose,
  onSubmit,
  canSubmit,
}: {
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
  canSubmit: boolean;
}) {
  const { t } = useTranslation(['russiaRequests', 'common']);
  const { confirmDestructive } = useEditorGuard();
  return (
    <div
      style={{
        marginTop: 6,
        marginLeft: -16,
        marginRight: -16,
        marginBottom: -16,
        padding: '12px 16px',
        borderTop: '1px solid var(--border-2)',
        display: 'flex',
        gap: 8,
        justifyContent: 'flex-end',
      }}
    >
      <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
        {t('common:actions.cancel')}
      </Button>
      <Button
        kind="primary"
        icon="plus"
        disabled={!canSubmit}
        onClick={() => {
          void onSubmit();
        }}
        data-testid="russia-request-drawer-carriers-add-submit"
      >
        {t('drawer.carriers.add.submit')}
      </Button>
    </div>
  );
}
